/**
 * gs1Parser — pure, dependency-free GS1 DataMatrix / GS1-128 decoder.
 *
 * Decodes the raw content of a GS1 DataMatrix (the per-lot 2D code on pharma
 * packaging) into its Application Identifiers (AIs) and maps the supported ones
 * onto the Item Master schema used by the Warehouse Receiving scanning module.
 *
 * Two input forms are supported (scanners emit either):
 *   1. FNC1 / GS separated:   "0106285128023429" + GS + "10CB0423" ...
 *      (FNC1 is ASCII 29, "\x1D"; it terminates variable-length AIs.)
 *   2. Human-readable parens:  "(01)06285128023429(10)CB0423(11)250227..."
 *
 * Supported AIs (see spec table in the build prompt):
 *   01  GTIN              fixed   14 digits           -> gtin
 *   10  Batch/Lot         variable up to 20, FNC1     -> batchLot
 *   11  Production/MFG     fixed   6 digits (YYMMDD)   -> mfgDate / mfgDateIso
 *   17  Expiry/EXP        fixed   6 digits (YYMMDD)   -> expDate / expDateIso
 *   21  Serial number     variable up to 20, FNC1     -> serialNumber
 *
 * Any other AI is preserved under `unknownAIs` and never crashes the parse.
 *
 * The module is intentionally framework-free so it can be unit-tested in
 * isolation and reused on the server for validation if needed.
 */

/** ASCII 29 — the GS1 FNC1 / Group Separator that terminates variable AIs. */
export const GS = '\x1D';

/** AIs we map onto the Item Master schema. */
export const SUPPORTED_AIS = ['01', '10', '11', '17', '21'] as const;
export type SupportedAi = (typeof SUPPORTED_AIS)[number];

/**
 * Fixed-length AIs and the number of data characters that follow them.
 * For these, no separator is needed — read exactly this many characters.
 */
const FIXED_LENGTH_AIS: Record<string, number> = {
  '01': 14, // GTIN
  '11': 6, // Production date YYMMDD
  '17': 6, // Expiry date YYMMDD
  // A few other common fixed-length date/measure AIs, so linear parsing of
  // real-world codes that interleave them does not desync. Values are kept as
  // "unknown" fields but their length is respected.
  '13': 6, // Packaging date
  '15': 6, // Best-before date
  '16': 6, // Sell-by date
};

/** Max data length for the variable AIs we care about (per spec). */
const VARIABLE_MAX_LEN: Record<string, number> = {
  '10': 20,
  '21': 20,
};

export interface Gs1Field {
  ai: string;
  value: string;
}

export interface Gs1ParseOptions {
  /**
   * Century pivot for YY → YYYY. Years `00..pivot` map to 20YY, `pivot+1..99`
   * map to 19YY. Default 50 (GS1 / pharma-future-safe: 00–50 → 20YY, 51–99 → 19YY).
   */
  centuryPivot?: number;
}

export interface Gs1ParseResult {
  /** GTIN (AI 01) — 14 numeric digits when valid. */
  gtin?: string;
  /** Batch/Lot (AI 10). */
  batchLot?: string;
  /** Serial number (AI 21). */
  serialNumber?: string;
  /** Production date (AI 11) — raw 6-digit YYMMDD, as printed/exported. */
  mfgDate?: string;
  /** Production date as ISO YYYY-MM-DD (display/secondary). */
  mfgDateIso?: string;
  /** Expiry date (AI 17) — raw 6-digit YYMMDD. */
  expDate?: string;
  /** Expiry date as ISO YYYY-MM-DD. */
  expDateIso?: string;
  /** Every recognized AI in the order it appeared. */
  fields: Gs1Field[];
  /** AIs outside the supported set — kept, never dropped. */
  unknownAIs: Gs1Field[];
  /** Non-fatal validation problems (malformed GTIN, unparseable date, etc.). */
  errors: string[];
  /** True when there are no blocking validation errors. */
  isValid: boolean;
  /** The normalized input actually parsed (after stripping symbology id / FNC1). */
  raw: string;
}

/** Leading symbology identifiers some scanners prepend (e.g. "]d2", "]C1"). */
const SYMBOLOGY_ID = /^\][A-Za-z]\d/;

/**
 * Strip a leading AIM symbology identifier and any leading FNC1/GS so the body
 * starts at the first AI.
 */
function normalizeInput(input: string): string {
  let s = input ?? '';
  // Trim surrounding whitespace but NOT internal GS chars.
  s = s.replace(/^\s+/, '').replace(/\s+$/, '');
  if (SYMBOLOGY_ID.test(s)) s = s.slice(3);
  // Drop any leading FNC1/GS the scanner reports before the first AI.
  while (s.startsWith(GS)) s = s.slice(1);
  return s;
}

function isParenForm(s: string): boolean {
  return s.includes('(');
}

/** Trim trailing GS and stray control/whitespace a scanner may append. */
function cleanValue(v: string): string {
  // Trim trailing GS / whitespace without embedding control chars in a regex.
  let end = v.length;
  while (end > 0 && (v.charCodeAt(end - 1) === 29 || v.charCodeAt(end - 1) <= 32)) end -= 1;
  return v.slice(0, end);
}

/** Parse the "(01)...(10)..." human-readable form. */
function parseParenForm(s: string): Gs1Field[] {
  const fields: Gs1Field[] = [];
  const re = /\((\d{2,4})\)([^(]*)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    fields.push({ ai: m[1], value: cleanValue(m[2]) });
  }
  return fields;
}

/**
 * Parse the concatenated FNC1/GS form. Walks the string AI by AI, honoring
 * fixed lengths and reading variable AIs up to a GS or end-of-string.
 */
function parseLinearForm(s: string): Gs1Field[] {
  const fields: Gs1Field[] = [];
  let i = 0;
  const n = s.length;
  while (i < n) {
    if (s[i] === GS) {
      i += 1;
      continue;
    }
    // AIs in our supported set are all 2-digit. Read a 2-digit AI prefix.
    const ai = s.slice(i, i + 2);
    if (!/^\d{2}$/.test(ai)) {
      // Not a numeric AI prefix — cannot continue structured parse.
      break;
    }
    i += 2;
    const fixed = FIXED_LENGTH_AIS[ai];
    if (fixed != null) {
      const value = s.slice(i, i + fixed);
      fields.push({ ai, value });
      i += fixed;
    } else {
      // Variable-length: read until GS or end-of-string.
      let end = s.indexOf(GS, i);
      if (end === -1) end = n;
      fields.push({ ai, value: cleanValue(s.slice(i, end)) });
      i = end;
    }
  }
  return fields;
}

const DIGITS_14 = /^\d{14}$/;

/**
 * Convert a 6-digit YYMMDD to ISO YYYY-MM-DD.
 * Per GS1, a day of "00" means "last day of the month" — handled here so we
 * never emit an invalid `...-00`. Returns null when the value is unparseable.
 */
export function yymmddToIso(yymmdd: string, centuryPivot = 50): string | null {
  if (!/^\d{6}$/.test(yymmdd)) return null;
  const yy = Number(yymmdd.slice(0, 2));
  const mm = Number(yymmdd.slice(2, 4));
  let dd = Number(yymmdd.slice(4, 6));
  if (mm < 1 || mm > 12) return null;
  const year = yy <= centuryPivot ? 2000 + yy : 1900 + yy;
  if (dd === 0) {
    // Last day of month.
    dd = new Date(Date.UTC(year, mm, 0)).getUTCDate();
  }
  if (dd < 1 || dd > 31) return null;
  const mmStr = String(mm).padStart(2, '0');
  const ddStr = String(dd).padStart(2, '0');
  return `${year}-${mmStr}-${ddStr}`;
}

/**
 * Parse a raw GS1 DataMatrix / GS1-128 string into structured fields mapped to
 * the Item Master schema. Never throws — malformed input surfaces via `errors`.
 */
export function parseGs1(input: string, opts: Gs1ParseOptions = {}): Gs1ParseResult {
  const centuryPivot = opts.centuryPivot ?? 50;
  const raw = normalizeInput(input);

  const result: Gs1ParseResult = {
    fields: [],
    unknownAIs: [],
    errors: [],
    isValid: true,
    raw,
  };

  if (!raw) {
    result.errors.push('Empty code');
    result.isValid = false;
    return result;
  }

  const fields = isParenForm(raw) ? parseParenForm(raw) : parseLinearForm(raw);
  if (fields.length === 0) {
    result.errors.push('No GS1 Application Identifiers found');
    result.isValid = false;
    return result;
  }
  result.fields = fields;

  for (const { ai, value } of fields) {
    switch (ai) {
      case '01': {
        result.gtin = value;
        if (!DIGITS_14.test(value)) {
          result.errors.push(`Malformed GTIN (AI 01): expected 14 digits, got "${value}"`);
          result.isValid = false;
        }
        break;
      }
      case '10': {
        result.batchLot = value;
        if (value.length > VARIABLE_MAX_LEN['10']) {
          result.errors.push(`Batch/Lot (AI 10) exceeds 20 characters`);
        }
        break;
      }
      case '21': {
        result.serialNumber = value;
        if (value.length > VARIABLE_MAX_LEN['21']) {
          result.errors.push(`Serial (AI 21) exceeds 20 characters`);
        }
        break;
      }
      case '11':
      case '17': {
        const iso = yymmddToIso(value, centuryPivot);
        if (ai === '11') {
          result.mfgDate = value;
          if (iso) result.mfgDateIso = iso;
        } else {
          result.expDate = value;
          if (iso) result.expDateIso = iso;
        }
        if (!iso) {
          result.errors.push(`Unparseable ${ai === '11' ? 'MFG' : 'EXP'} date (AI ${ai}): "${value}"`);
          result.isValid = false;
        }
        break;
      }
      default:
        result.unknownAIs.push({ ai, value });
    }
  }

  return result;
}

/**
 * Build the idempotency / duplicate-guard key for a parsed scan:
 * `gtin + serialNumber`, falling back to `gtin + batchLot` when no serial.
 */
export function scanDedupeKey(r: Pick<Gs1ParseResult, 'gtin' | 'serialNumber' | 'batchLot'>): string {
  const gtin = r.gtin ?? '';
  if (r.serialNumber) return `${gtin}|${r.serialNumber}`;
  return `${gtin}|${r.batchLot ?? ''}`;
}

/**
 * Normalize an EAN-13 / GTIN-13 linear barcode to GTIN-14 (left-pad a zero).
 * Used by the lookup layer to cross-check a scanned 1D barcode against the
 * DataMatrix GTIN (AI 01), which is always 14 digits.
 */
export function toGtin14(barcode: string): string {
  const d = (barcode ?? '').replace(/\D/g, '');
  if (d.length === 14) return d;
  if (d.length === 13) return '0' + d;
  if (d.length === 12) return '00' + d; // UPC-A
  return d;
}
